import { Fragment, useEffect, useRef, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEnqueueScrape, useScrapeRuns } from '../hooks/useScrapeRuns'
import { useMe } from '../hooks/useAuth'
import { hasTokens } from '../lib/auth'
import { canTriggerScrape } from '../lib/permissions'
import { TableSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import {
  ApiError,
  SCRAPE_SOURCE_LABEL,
  type ScrapeRun,
  type ScrapeSource,
} from '../lib/api'

type AdminScrapeSearch = {
  source?: string
}

export const Route = createFileRoute('/admin/scrape')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): AdminScrapeSearch => {
    const toOptStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length > 0 ? v : undefined
    return {
      source: toOptStr(search.source),
    }
  },
  component: AdminScrapePage,
})

// Derivato da SCRAPE_SOURCE_LABEL (già garantito completo/corretto dal
// compilatore via Record<ScrapeSource, string>) invece di un elenco a parte —
// prima un ScrapeSource dimenticato qui compilava pulito e semplicemente non
// comparsiva mai come bottone/opzione, ora è impossibile che diverga.
const SOURCE_OPTIONS = Object.keys(SCRAPE_SOURCE_LABEL) as ScrapeSource[]

const STATUS_BADGE: Record<string, string> = {
  success: 'badge-green',
  partial: 'badge-orange',
  failed: 'badge-red',
  running: 'badge-blue',
  captcha_blocked: 'badge-red',
}

function statusBadgeClass(status: string): string {
  return STATUS_BADGE[status] ?? 'badge-gray'
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return 'running…'
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return '—'
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000))
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

function errorStrings(errors: unknown[] | null): string[] {
  if (!errors || !Array.isArray(errors)) return []
  return errors.map((e) => {
    if (typeof e === 'string') return e
    if (e && typeof e === 'object') {
      try {
        return JSON.stringify(e)
      } catch {
        return String(e)
      }
    }
    return String(e)
  })
}

type Banner = { type: 'success' | 'error'; message: string }

function AdminScrapePage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/admin/scrape' })
  const { data: me } = useMe()
  const allowedToTrigger = canTriggerScrape(me)

  const [banner, setBanner] = useState<Banner | null>(null)
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data, isPending, isError, error, refetch, isFetching, dataUpdatedAt } =
    useScrapeRuns(search.source, 50)

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null

  useEffect(() => {
    return () => {
      if (bannerTimer.current) clearTimeout(bannerTimer.current)
    }
  }, [])

  function showBanner(b: Banner) {
    setBanner(b)
    if (bannerTimer.current) clearTimeout(bannerTimer.current)
    bannerTimer.current = setTimeout(() => setBanner(null), 4000)
  }

  const enqueueMut = useEnqueueScrape()

  function onTrigger(src: ScrapeSource) {
    enqueueMut.mutate(src, {
      onSuccess: (resp) => {
        showBanner({
          type: 'success',
          message: `Job enqueued for ${resp.source} (id: ${resp.job_id})`,
        })
      },
      onError: (err: ApiError) => {
        if (err.status === 403) {
          showBanner({
            type: 'error',
            message:
              'Non hai permesso di lanciare scrape (solo supervisore/analista/responsabile)',
          })
        } else {
          showBanner({
            type: 'error',
            message: err.message || "Errore durante l'avvio dello scrape",
          })
        }
      },
    })
  }

  function setSearch(patch: Partial<AdminScrapeSearch>) {
    void navigate({
      search: (s: AdminScrapeSearch) => ({ ...s, ...patch }),
    })
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const mutationPending = enqueueMut.isPending

  return (
    <div className="page">
      <div className="page-header">
        <h1>Scrape runs</h1>
        {data && (
          <span className="muted">
            {data.length} {data.length === 1 ? 'run' : 'run'}
          </span>
        )}
      </div>

      {banner && (
        <div
          className={banner.type === 'success' ? 'banner-success' : 'banner-error'}
        >
          {banner.message}
        </div>
      )}

      {allowedToTrigger ? (
        <div className="scrape-actions">
          {SOURCE_OPTIONS.map((src) => (
            <button
              key={src}
              type="button"
              disabled={mutationPending}
              onClick={() => onTrigger(src)}
            >
              Esegui {SCRAPE_SOURCE_LABEL[src]}
            </button>
          ))}
        </div>
      ) : (
        <div className="banner-error">
          Solo supervisore/analista/responsabile possono lanciare scrape
        </div>
      )}

      <div className="filters-row">
        <select
          value={search.source ?? ''}
          onChange={(e) =>
            setSearch({ source: e.target.value || undefined })
          }
          className="filter-select"
          aria-label="Filter by source"
        >
          <option value="">-- all sources --</option>
          {SOURCE_OPTIONS.map((src) => (
            <option key={src} value={src}>
              {src}
            </option>
          ))}
        </select>
        <span className="muted" style={{ marginLeft: 'auto' }}>
          Auto-refresh ogni 15s
          {lastUpdated && ` · ultimo: ${formatDateTime(lastUpdated.toISOString())}`}
          {isFetching && ' · aggiornamento…'}
        </span>
      </div>

      {isPending && <TableSkeleton rows={6} cols={10} />}
      {isError && (
        <div className="banner-error">
          {error?.message ?? 'Errore nel caricamento scrape runs'}
          <button
            type="button"
            className="secondary"
            style={{ marginLeft: 12 }}
            onClick={() => void refetch()}
          >
            Riprova
          </button>
        </div>
      )}

      {data && data.length === 0 && !isPending && (
        <EmptyState
          icon="🤖"
          title="Nessuno scrape ancora"
          body={
            allowedToTrigger
              ? 'Lancia uno scrape dai pulsanti sopra.'
              : 'Non ci sono ancora scrape runs registrati.'
          }
        />
      )}

      {data && data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Status</th>
                <th>Started</th>
                <th>Finished</th>
                <th>Duration</th>
                <th>Found</th>
                <th>New</th>
                <th>Updated</th>
                <th>Failed</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r: ScrapeRun) => {
                const errs = errorStrings(r.errors)
                const isExpanded = expanded.has(r.id)
                const badgeClass = statusBadgeClass(r.status)
                const badgeExtra = r.status === 'running' ? ' pulse' : ''
                return (
                  <Fragment key={r.id}>
                    <tr>
                      <td>{r.source}</td>
                      <td>
                        <span className={`badge ${badgeClass}${badgeExtra}`}>
                          {r.status}
                        </span>
                      </td>
                      <td>{formatDateTime(r.started_at)}</td>
                      <td>{formatDateTime(r.finished_at)}</td>
                      <td>{formatDuration(r.started_at, r.finished_at)}</td>
                      <td className="cell-num">{r.total_found}</td>
                      <td className="cell-num">{r.new_items}</td>
                      <td className="cell-num">{r.updated_items}</td>
                      <td className="cell-num">{r.failed_items}</td>
                      <td>
                        {errs.length > 0 ? (
                          <button
                            type="button"
                            className="secondary"
                            style={{
                              padding: '2px 8px',
                              fontSize: 12,
                              color: '#991b1b',
                              borderColor: '#fecaca',
                            }}
                            onClick={() => toggleExpanded(r.id)}
                          >
                            ✕ {errs.length} errori
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                    {isExpanded && errs.length > 0 && (
                      <tr>
                        <td colSpan={10}>
                          <ul className="error-list">
                            {errs.slice(0, 5).map((msg, i) => (
                              <li key={i}>{msg}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
